import React, { FC, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Tooltip from '@mui/material/Tooltip';
import { FormikContext } from 'formik';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { TypographyVariant } from '@mui/material';

import { ToggleDialog } from '../IFormModal';
import WarningConfirmModal from '../../../../WarningConfirmModal';

const modalFormTitleStyles = makeStyles((theme) => {
    return {
        dialogTitleStyle: {
            padding: theme.spacing(1.5, 2, 1.5, 3),
            // Same colour as the top bar (palette.brandHeader).
            backgroundColor: (theme.palette as any).brandHeader ?? theme.palette.secondary.main,
            color: '#fff',
            cursor: 'move',
            // The dialog paper's own radius (MuiDialog paper uses --radius-lg):
            // a rounder title left the white paper showing in both corners.
            borderRadius: 'var(--radius-lg, 10px) var(--radius-lg, 10px) 0 0',
            // Where the title's edge sits on the paper's, both are anti-aliased
            // and the white paper bled through as a hairline. A 1px ring in the
            // title's own colour covers it.
            boxShadow: `0 0 0 1px ${(theme.palette as any).brandHeader ?? theme.palette.secondary.main}`,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.spacing(1)
        },
        titleText: {
            fontWeight: 600,
            fontSize: '1.1rem',
            lineHeight: 1.4,
            letterSpacing: '0.01em'
        },
        closeButton: {
            color: '#fff',
            padding: theme.spacing(0.75),
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.15)'
            }
        }
    };
});

interface Props {
    children?: ReactNode;
    titleComponent?: ReactNode;
    toggleDialog: ToggleDialog;
    title: string;
    overrideClasses?: string;
    variant?: TypographyVariant | undefined;
    titleBarStyle?: React.CSSProperties;
    /** When defined, renders an enlarge icon next to the close X. Already bound
     *  to the form's current values by the caller. */
    onExpand?: () => void;
    /**
     * Optional: hand the unsaved-changes guard to the parent.
     *
     * When supplied, the X reports a close *request* instead of closing, and
     * this component renders no discard prompt of its own — the parent owns
     * both. TabsDialogForm passes it because the X is only one of four ways out
     * of a tabbed form (Cancel and Escape bypass anything rooted here), and a
     * guard that only covers one exit is worse than none: it teaches the user
     * they will be warned.
     *
     * Left undefined by the ~70 other callers, which keep the self-hosted
     * prompt below exactly as it was.
     */
    onRequestClose?: () => void;
}

const ModalFormTitle: FC<Props> = (props: Props) => {
    const {
        title,
        titleComponent,
        toggleDialog,
        children = null,
        overrideClasses = '',
        variant,
        titleBarStyle,
        onExpand,
        onRequestClose
    } = props;
    const classes = modalFormTitleStyles();

    // useContext(FormikContext) === useFormikContext() // true
    const formProps = useContext(FormikContext);

    // Mirrors WorkspaceHeader: Formik's `dirty` can flip from programmatic
    // setFieldValue on tab-mount, so gate the discard prompt on a real user
    // interaction. Tab clicks and dialog chrome don't count.
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const submitCountRef = useRef(formProps?.submitCount ?? 0);

    useEffect(() => {
        const handler = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            // Tab clicks are navigation, not edits — they shouldn't flip the
            // form to "user interacted" because tab mounts often run
            // programmatic setFieldValue (defaults, derived values) which
            // would otherwise be misread as a user edit and open the discard
            // prompt on the next close. Cover both MUI Tabs and our custom
            // sidebar tabs (TabsDialogForm renders divs with data-form-tab).
            if (target.closest?.('.MuiTab-root')) return;
            if (target.closest?.('[data-form-tab]')) return;
            if (target.closest?.('.draggable-dialog-title')) return;
            // The Change Rate dialog is a transient rate tool rendered as a React
            // child of the form (portal events bubble through). Typing in it — e.g.
            // to preview the USD, then Cancel — must not flag the form as edited,
            // or exiting would wrongly prompt to save unsaved changes.
            if (target.closest?.('.change-rate-dialog')) return;
            setHasUserInteracted(true);
        };
        // Listen only to events that semantically represent a value change.
        // Earlier we also listened to `keydown` and `click`, but those fire
        // for navigation keys (Tab/Escape/arrows) and for clicks on
        // non-editing UI (buttons, dropdown openers) — both wrongly flagged
        // the form as edited even when nothing changed, so closing without
        // actually typing anything would still open the discard prompt.
        // `input` (text fields) + `change` (select/checkbox/radio) cover
        // the real edit events without the noise.
        document.addEventListener('input', handler, true);
        document.addEventListener('change', handler, true);
        return () => {
            document.removeEventListener('input', handler, true);
            document.removeEventListener('change', handler, true);
        };
    }, []);

    // Snapshot values once mount-time effects settle, then use it as the
    // dirty-check anchor instead of Formik's `initialValues`. Many service
    // forms run useEffect → setFieldValue at mount to populate defaults /
    // derived values, which makes Formik's `dirty` permanently true even
    // before the user has touched anything. Comparing against this stable
    // baseline lets "type then revert to original" correctly clear dirty.
    const baselineRef = useRef<string | null>(null);
    useEffect(() => {
        if (!formProps) return;
        if (baselineRef.current !== null) return;
        // Defer one tick so any mount-time setFieldValue calls land before
        // we capture the snapshot.
        const t = setTimeout(() => {
            try {
                baselineRef.current = JSON.stringify(formProps.values ?? {});
            } catch {
                baselineRef.current = '';
            }
        }, 0);
        return () => clearTimeout(t);
    }, [formProps]);

    // Re-baseline after a successful save so subsequent edits are measured
    // from the saved state.
    useEffect(() => {
        const sc = formProps?.submitCount ?? 0;
        if (sc > submitCountRef.current) {
            submitCountRef.current = sc;
            try {
                baselineRef.current = JSON.stringify(formProps?.values ?? {});
            } catch {
                baselineRef.current = '';
            }
            setHasUserInteracted(false);
        }
    }, [formProps?.submitCount, formProps?.values]);

    // Compare against the baseline. Used in place of Formik's `dirty` so we
    // can ignore programmatic mount-time mutations.
    const reallyDirty = useMemo(() => {
        if (baselineRef.current === null) return false;
        try {
            return JSON.stringify(formProps?.values ?? {}) !== baselineRef.current;
        } catch {
            return !!formProps?.dirty;
        }
    }, [formProps?.values, formProps?.dirty]);

    // When the form is no longer divergent from the baseline (user reverted
    // their edit), drop the interaction flag so a subsequent close doesn't
    // prompt.
    useEffect(() => {
        if (!reallyDirty && hasUserInteracted) setHasUserInteracted(false);
    }, [reallyDirty, hasUserInteracted]);

    const isDirty = reallyDirty && hasUserInteracted;
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

    const handleClose = useCallback(() => {
        if (isDirty) {
            setDiscardConfirmOpen(true);
            return;
        }
        toggleDialog();
    }, [isDirty, toggleDialog]);

    const handleConfirmDiscard = useCallback(() => {
        setDiscardConfirmOpen(false);
        toggleDialog();
    }, [toggleDialog]);

    return (
        <>
            <div className={`${classes.dialogTitleStyle} ${overrideClasses} draggable-dialog-title`} style={{ transition: 'background-color 0.3s ease', ...titleBarStyle }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {titleComponent ? (
                        titleComponent
                    ) : (
                        <Typography
                            variant={variant}
                            color="inherit"
                            className={!variant ? classes.titleText : undefined}
                        >
                            {title}
                        </Typography>
                    )}
                    {children}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {onExpand && (() => {
                        // Full-page mode is URL-driven (by service _id), so it can
                        // only carry a SAVED service. For a new/unsaved service
                        // there's no _id to reopen by — disable the button (with a
                        // tooltip) instead of silently closing the form and losing
                        // the in-progress entries.
                        const isUnsavedService = !(formProps?.values as any)?._id;
                        return (
                            <Tooltip
                                title={
                                    isUnsavedService
                                        ? 'Save the service to open it in full page'
                                        : 'Open in full page'
                                }
                            >
                                {/* span wrapper so the Tooltip still works while the button is disabled */}
                                <span>
                                    <IconButton
                                        className={classes.closeButton}
                                        onClick={onExpand}
                                        disabled={formProps?.isSubmitting || isUnsavedService}
                                        size="small"
                                    >
                                        <OpenInFullIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })()}
                    <IconButton
                        className={classes.closeButton}
                        onClick={onRequestClose || handleClose}
                        disabled={formProps?.isSubmitting}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
            {/* Suppressed when the parent owns the guard, or the user would get
                two stacked "Unsaved changes" dialogs off one click. */}
            <WarningConfirmModal
                open={!onRequestClose && discardConfirmOpen}
                toggleModal={setDiscardConfirmOpen}
                onConfirm={handleConfirmDiscard}
                confirmText="Discard"
                cancelText="Keep editing"
                title="Unsaved changes"
                subtitle="You have unsaved changes. Close without saving?"
            />
        </>
    );
};

export default memo(ModalFormTitle);
